import React, { useEffect, useState } from "react";
import { Users, Send, LayoutTemplate, Activity as ActivityIcon, UserCheck, CalendarPlus } from "lucide-react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ContactsPanel from "./ContactsPanel";
import TemplatesPanel from "./TemplatesPanel";
import CampaignsPanel from "./CampaignsPanel";
import ActivityPanel from "./ActivityPanel";
import ListsPanel from "./ListsPanel";
import AutomationsPanel from "./AutomationsPanel";

export default function CrmHubPanel() {
  const [stats, setStats] = useState(null);

  const refreshStats = () => {
    api.get("/admin/crm/stats").then((r) => setStats(r.data));
  };

  useEffect(() => {
    refreshStats();
  }, []);

  return (
    <div data-testid="crm-hub-panel">
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
          {[
            ["Total Contacts", stats.total_contacts, Users],
            ["Subscribed", stats.subscribed_contacts, UserCheck],
            ["New This Week", stats.new_this_week, CalendarPlus],
            ["Templates", stats.templates_count, LayoutTemplate],
            ["Campaigns Sent", stats.campaigns_sent, Send],
          ].map(([label, val, Icon]) => (
            <div key={label} className="glass-panel rounded-xl p-4" data-testid={`crm-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
              <Icon className="w-4 h-4 text-indigo-400 mb-2" />
              <p className="font-heading text-2xl font-bold text-white">{val}</p>
              <p className="text-xs text-slate-400">{label}</p>
            </div>
          ))}
        </div>
      )}

      {stats && !stats.twilio_configured && (
        <div className="glass-panel rounded-xl p-3 mb-6 text-xs text-amber-300 flex items-center gap-2" data-testid="crm-twilio-not-configured-banner">
          <ActivityIcon className="w-3.5 h-3.5 shrink-0" />
          Email (Resend) is live and ready to send. SMS &amp; WhatsApp are structurally built and will switch on automatically once Twilio credentials are added.
        </div>
      )}

      <Tabs defaultValue="contacts">
        <TabsList className="bg-white/5 p-1.5 mb-6">
          <TabsTrigger value="contacts" data-testid="crm-subtab-contacts" className="data-[state=active]:bg-primary data-[state=active]:text-white">Contacts</TabsTrigger>
          <TabsTrigger value="lists" data-testid="crm-subtab-lists" className="data-[state=active]:bg-primary data-[state=active]:text-white">Lists</TabsTrigger>
          <TabsTrigger value="templates" data-testid="crm-subtab-templates" className="data-[state=active]:bg-primary data-[state=active]:text-white">Templates</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="crm-subtab-campaigns" className="data-[state=active]:bg-primary data-[state=active]:text-white">Campaigns</TabsTrigger>
          <TabsTrigger value="automations" data-testid="crm-subtab-automations" className="data-[state=active]:bg-primary data-[state=active]:text-white">Automations</TabsTrigger>
          <TabsTrigger value="activity" data-testid="crm-subtab-activity" className="data-[state=active]:bg-primary data-[state=active]:text-white">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="contacts" data-testid="crm-content-contacts">
          <ContactsPanel onContactsChanged={refreshStats} />
        </TabsContent>
        <TabsContent value="lists" data-testid="crm-content-lists">
          <ListsPanel />
        </TabsContent>
        <TabsContent value="templates" data-testid="crm-content-templates">
          <TemplatesPanel twilioConfigured={!!stats?.twilio_configured} onTemplatesChanged={refreshStats} />
        </TabsContent>
        <TabsContent value="campaigns" data-testid="crm-content-campaigns">
          <CampaignsPanel twilioConfigured={!!stats?.twilio_configured} onCampaignsChanged={refreshStats} />
        </TabsContent>
        <TabsContent value="automations" data-testid="crm-content-automations">
          <AutomationsPanel twilioConfigured={!!stats?.twilio_configured} />
        </TabsContent>
        <TabsContent value="activity" data-testid="crm-content-activity">
          <ActivityPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
